import React, { useEffect, useMemo, useRef, useState } from "react";
import { encodeProject } from "../utils/serialization";
import { VideoInteractionStatus, mapProjectToBackendPayload } from "../utils/backendPayload";
import {
  getCurrentUser,
  getStatusLabel,
  getVideoProjectByVideoId,
  saveVideoWork,
} from "../utils/dashboardStorage";
import { BuilderInteractionPreview } from "./builder/BuilderInteractionPreview";
import { BuilderDrawerForm } from "./builder/BuilderDrawerForm";
import {
  DEFAULT_PROJECT,
  MAX_IMAGE_SIZE_BYTES,
  clamp,
  getDefaultDraft,
  interactionLabel,
  makeId,
  readFileAsDataUrl,
  safeTrim,
  toSecondsMsLabel
} from "./builder/builderUtils";
import  '../styles.css'
import { useLocation } from "react-router-dom";
import { mapApiInteractions } from "../utils/interactionMapper";
import { buildSaveInteractionsPayload } from "../utils/buildSaveInteractionsPayload";
import { addAchVideoInteraction, deleteInteractionVideo, updateInteractionVideo } from "../../../api/VideoInteractions/videoInteractions";
import { buildUpdateInteractionPayload } from "../utils/buildUpdateInteractionPayload";
import { useHistory } from "react-router-dom";
import { buildInreviewInteractionPayload } from "../utils/buildInreviewInteractionPayload";
import { uploadImageToCMS } from "../utils/uploadImageToCMS";
import { ToastContainer, toast } from "react-toastify";

export const InteractionStatusLabels = {
  1: "Yapılacak",
  2: "Yapılıyor",
  3: "İnceleme Bekliyor",
  4: "Reddedildi",
  5: "Onaylandı",
  6: "Hatalı"
};

export const InteractionStatusColors = {
  1: "#3b82f6", // blue
  2: "#f59e0b", // amber
  3: "#eab308", // yellow
  4: "#ef4444", // red
  5: "#22c55e", // green
  6: "#6b7280"  // gray
};

function getBuilderStorageKey(videoId) {
  return `interactive-video-builder:builder-state:v2:${videoId ?? "none"}`;
}

function loadPersistedBuilderState(videoId) {
  if (typeof window === "undefined") return null;

  const raw = window.localStorage.getItem(getBuilderStorageKey(videoId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.project || !parsed.draft || !parsed.draftPosition) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export default function Builder({
  currentUser,
  onBack,
  onSaved
})
{
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem("user"));
  const userAdminToken = sessionStorage.getItem("jwtP");
  const userRole = user.Role === "ContentManagers" ? "admin" : "teacher"
  const location = useLocation();
const builderData = location.state?.builderData ?? null;
const selectedCourse = location.state?.selectedCourse;
const persisted = useMemo(
  () => loadPersistedBuilderState(builderData?.AchievementVideoId ?? null),
  [builderData?.AchievementVideoId]
);

  const sessionUser = currentUser ?? getCurrentUser();

  const [project, setProject] = useState(
    persisted?.project ??
      (builderData
        ? {
            title: builderData.AchievementVideoTitle,
            videoUrl:  builderData.VideoUrl,
            interactions: mapApiInteractions(builderData.VideoInteractionDetails)
          }
        : DEFAULT_PROJECT)
  );
  
  const [draft, setDraft] = useState(persisted?.draft ?? getDefaultDraft());

  const previewRef = useRef(null);
  const previewStageRef = useRef(null);
  const dragPointerActiveRef = useRef(false);

  const [previewTimeSec, setPreviewTimeSec] = useState(0);
  const [draftPosition, setDraftPosition] = useState(
    persisted?.draftPosition ?? { xPct: 50, yPct: 50 }
  );

  const [formError, setFormError] = useState("");
  const [isInteractionDrawerOpen, setIsInteractionDrawerOpen] = useState(false);
  const [isMediaAccordionOpen, setIsMediaAccordionOpen] = useState(false);
  const [editingInteractionId, setEditingInteractionId] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedPreviewInteractionId, setSelectedPreviewInteractionId] =
    useState(null);
    const [currentVideoStatus, setCurrentVideoStatus] = useState(
      builderData?.VideoInteractionStatus ?? "Todo"
    );
    
  const [saveInfoText, setSaveInfoText] = useState("");
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
const [isSaving, setIsSaving] = useState(false);
const [deletedInteractionIds, setDeletedInteractionIds] = useState([]);
const [videoDuration, setVideoDuration] = useState(0);
const [statusUpdating, setStatusUpdating] = useState({});
const [isUploading, setIsUploading] = useState(false);

  

  useEffect(() => {
    if (!builderData) return;
  
    setProject({
      title: builderData.AchievementVideoTitle,
      videoUrl: builderData.VideoUrl,
      interactions: mapApiInteractions(builderData.VideoInteractionDetails)
    });
  
  }, [builderData]);
  
  const hasVideoUrl = Boolean((project.videoUrl || "").trim());
  const isVideoEditable =
    currentVideoStatus !== "InReview" && currentVideoStatus !== "Done";

  const dragDropSlots = useMemo(() => {
    return (draft.dragDropSlotsText || "")
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
  }, [draft.dragDropSlotsText]);

  const dragDropItems = useMemo(() => {
    return (draft.dragDropItemsText || "")
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
  }, [draft.dragDropItemsText]);

  useEffect(() => {
    setDraft((prev) => {
      const normalized = Array.from({ length: dragDropSlots.length }, (_, idx) => {
        const values = prev.dragDropCorrectMap?.[idx] ?? [];
        return values.filter(
          (value, valueIdx, arr) =>
            Number.isInteger(value) &&
            value >= 0 &&
            value < dragDropItems.length &&
            arr.indexOf(value) === valueIdx
        );
      });

      const sameLength =
        normalized.length === (prev.dragDropCorrectMap?.length ?? 0);

      const sameValues =
        sameLength &&
        normalized.every((value, idx) => {
          const prevGroup = prev.dragDropCorrectMap?.[idx] ?? [];
          return (
            value.length === prevGroup.length &&
            value.every((v, i) => v === prevGroup[i])
          );
        });

      if (sameValues) return prev;
      return { ...prev, dragDropCorrectMap: normalized };
    });
  }, [dragDropSlots.length, dragDropItems.length]);

  useEffect(() => {
    const onPointerMove = (event) => {
      if (!dragPointerActiveRef.current || !previewStageRef.current) return;

      const rect = previewStageRef.current.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const xPct = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const yPct = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      setDraftPosition({ xPct, yPct });
    };

    const onPointerUp = () => {
      dragPointerActiveRef.current = false;
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  useEffect(() => {
    if (!(project.videoUrl || "").trim()) {
      setIsInteractionDrawerOpen(false);
    }
  }, [project.videoUrl]);

  // useEffect(() => {
  //   if (!videoContext) {
  //     setProject(DEFAULT_PROJECT);
  //     setDraft(getDefaultDraft());
  //     setDraftPosition({ xPct: 50, yPct: 50 });
  //     setCurrentVideoStatus("Todo");
  //     setEditingInteractionId(null);
  //     setSelectedPreviewInteractionId(null);
  //     setFormError("");
  //     setSaveInfoText("");
  //     return;
  //   }

  //   const savedProject = getVideoProjectByVideoId(videoContext.id);
  //   const nextProject = {
  //     title:
  //       savedProject?.title?.trim() || videoContext.title || "Adsız Etkileşimli Video",
  //     videoUrl: videoContext.videoUrl,
  //     interactions: savedProject?.interactions ?? []
  //   };
  //   setProject(nextProject);
  //   setDraft(getDefaultDraft());
  //   setDraftPosition({ xPct: 50, yPct: 50 });
  //   setCurrentVideoStatus(videoContext.status);
  //   setEditingInteractionId(null);
  //   setSelectedPreviewInteractionId(null);
  //   setFormError("");
  //   setSaveInfoText("");
  // }, [videoContext]);


  useEffect(() => {
    if (typeof window === "undefined") return;

    const payload = { project, draft, draftPosition,deletedInteractionIds };
    window.localStorage.setItem(
      getBuilderStorageKey(builderData?.AchievementVideoId ?? null),
      JSON.stringify(payload)
    );
  }, [project, draft, draftPosition]);

  const exportData = useMemo(() => encodeProject(project), [project]);
  const backendPayload = useMemo(() => mapProjectToBackendPayload(project), [project]);
  const backendPayloadText = useMemo(
    () => JSON.stringify(backendPayload, null, 2),
    [backendPayload]
  );

  const embedUrl = `${window.location.origin}/embed?data=${exportData}`;
  const iframeSnippet = `<iframe src="${embedUrl}" width="960" height="540" style="border:0;" allow="autoplay; fullscreen"></iframe>`;
  const reactSnippet = `export function InteractiveLesson() {
  return <iframe src="${embedUrl}" width={960} height={540} style={{ border: 0 }} allow="autoplay; fullscreen" />;
}`;
  const reactNativeWebViewSnippet = `import { WebView } from "react-native-webview";

export default function InteractiveLessonWebView() {
  return <WebView source={{ uri: "${embedUrl}" }} />;
}`;

  const reactNativeVideoPayload = JSON.stringify(project, null, 2);

  const sortedInteractions = useMemo(() => {
    return [...(project.interactions || [])].sort((a, b) => a.timeSec - b.timeSec);
  }, [project.interactions]);

  const selectedPreviewInteraction = useMemo(() => {
    if (selectedPreviewInteractionId == null) return null;
    return (
      (project.interactions || []).find((item) => item.id === selectedPreviewInteractionId) ??
      null
    );
  }, [project.interactions, selectedPreviewInteractionId]);

  const fillDraftFromInteraction = (interaction) => {
    const nextDraft = getDefaultDraft(interaction.type);

    nextDraft.timeSec = interaction.timeSec;
    nextDraft.prompt = interaction.prompt;
    nextDraft.revealText = interaction.revealText;
    nextDraft.backgroundImageUrl = interaction.backgroundImageUrl ?? "";
    nextDraft.exampleImageUrl = interaction.exampleImageUrl ?? "";
    nextDraft.enableRetry = interaction.enableRetry ?? true
    nextDraft.showSolution = interaction.showSolution ?? true

    if (interaction.type === "multiple_choice") {
      nextDraft.mcSelectionMode = interaction.selectionMode;

      if (interaction.options && interaction.options.length > 0) {
        nextDraft.mcOptions = interaction.options.map((option) => ({
          text: option.text,
          isCorrect: option.isCorrect,
          tipText: option.tipText ?? "",
          selectedFeedback: option.selectedFeedback ?? "",
          unselectedFeedback: option.unselectedFeedback ?? ""
        }));
      } else if (interaction.choices && interaction.choices.length > 0) {
        nextDraft.mcOptions = interaction.choices.map((choice, idx) => ({
          text: choice,
          isCorrect: interaction.correctIndex === idx,
          tipText: "",
          selectedFeedback: "",
          unselectedFeedback: ""
        }));
      }
    } else if (interaction.type === "true_false") {
      nextDraft.trueFalseCorrect = interaction.correct;
    } else if (interaction.type === "fill_blank") {
      nextDraft.prompt = interaction.templateText ?? interaction.prompt;
    } else if (interaction.type === "info_hint") {
      nextDraft.hintDurationSec = interaction.durationSec;
    } else if (interaction.type === "word_bank_fill") {
      nextDraft.wordBankSentenceText = interaction.sentenceTemplate;
      nextDraft.wordBankDistractorsText = (interaction.distractorWords || []).join("\n");
    } else if (interaction.type === "drag_drop") {
      nextDraft.dragDropSlotsText = (interaction.slots || []).join("\n");
      nextDraft.dragDropItemsText = (interaction.items || []).join("\n");
      nextDraft.dragDropCorrectMap = (interaction.correctBySlot || []).map((group) => [
        ...group
      ]);
    }

    setDraft(nextDraft);
    setDraftPosition(interaction.position);
    setIsMediaAccordionOpen(
      Boolean(
        (interaction.backgroundImageUrl ?? "").trim() ||
          (interaction.exampleImageUrl ?? "").trim()
      )
    );
  };

  const addInteraction = () => {
    setFormError("");

    if (!draft.prompt.trim() && draft.type !== "info_hint" && draft.type !== "word_bank_fill") {
      setFormError("Lütfen önce soruyu yazın.");
      return;
    }

    let interaction;

    if (draft.type === "multiple_choice") {
      const options = draft.mcOptions
        .map((opt) => ({ ...opt, text: (opt.text || "").trim() }))
        .filter((opt) => opt.text);

      if (options.length < 2) {
        setFormError("Lütfen en az 2 seçenek ekleyin.");
        return;
      }

      const correctCount = options.filter((opt) => opt.isCorrect).length;
      if (correctCount === 0) {
        setFormError("Lütfen doğru cevabı seçin.");
        return;
      }

      if (draft.mcSelectionMode === "single" && correctCount !== 1) {
        setFormError("Tek doğru modunda yalnızca 1 doğru seçenek olmalıdır.");
        return;
      }
      const isNew = editingInteractionId == null;

      interaction = {
        id: editingInteractionId ?? makeId(),
        isNew,
        isDirty: true,
        type: "multiple_choice",
        timeSec: draft.timeSec,
        startTime: draft.timeSec,
        prompt: safeTrim(draft.prompt),
        revealText: safeTrim(draft.revealText),
        backgroundImageUrl: safeTrim(draft.backgroundImageUrl),
        exampleImageUrl: safeTrim(draft.exampleImageUrl),
        position: draftPosition,
        selectionMode: draft.mcSelectionMode,
        options,
        enableRetry: draft.enableRetry ?? true,
        showSolution: draft.showSolution ?? true

      };
    } else if (draft.type === "true_false") {
      interaction = {
        id: editingInteractionId ?? makeId(),
        type: "true_false",
        timeSec: draft.timeSec,
        startTime: draft.timeSec,
        prompt: safeTrim(draft.prompt),
        revealText: safeTrim(draft.revealText),
        backgroundImageUrl: safeTrim(draft.backgroundImageUrl),
        exampleImageUrl: safeTrim(draft.exampleImageUrl),
        position: draftPosition,
        correct: draft.trueFalseCorrect,
        enableRetry: draft.enableRetry ?? true,
        showSolution: draft.showSolution ?? true

      };
    } else if (draft.type === "fill_blank") {
      const blankMatches = Array.from(draft.prompt.matchAll(/\*([^*]+)\*/g));
      const blanks = blankMatches
        .map((match) =>
          (match[1] ?? "")
            .split("|")
            .map((v) => v.trim())
            .filter(Boolean)
        )
        .filter((answers) => answers.length > 0);

      if (blanks.length === 0) {
        setFormError("Boşluklar için *kelime* kullanın. Alternatifler için: *kelime|alternatif*");
        return;
      }

      interaction = {
        id: editingInteractionId ?? makeId(),
        type: "fill_blank",
        timeSec: draft.timeSec,
        startTime: draft.timeSec,
        prompt: safeTrim(draft.prompt),
        templateText: safeTrim(draft.prompt),
        blanks,
        revealText: safeTrim(draft.revealText),
        backgroundImageUrl: safeTrim(draft.backgroundImageUrl),
        exampleImageUrl: safeTrim(draft.exampleImageUrl),
        position: draftPosition,
        enableRetry: draft.enableRetry ?? true,
        showSolution: draft.showSolution ?? true

      };
    } else if (draft.type === "info_hint") {
      interaction = {
        id: editingInteractionId ?? makeId(),
        type: "info_hint",
        timeSec: draft.timeSec,
        startTime: draft.timeSec,
        prompt: safeTrim(draft.prompt),
        revealText: "",
        backgroundImageUrl: safeTrim(draft.backgroundImageUrl),
        exampleImageUrl: safeTrim(draft.exampleImageUrl),
        position: draftPosition,
        durationSec: Math.max(1, Math.floor(draft.hintDurationSec || 10))
      };
    } else if (draft.type === "word_bank_fill") {
      const correctWords = Array.from(
        (draft.wordBankSentenceText || "").matchAll(/\*([^*]+)\*/g)
      )
        .map((match) => (match[1] || "").trim())
        .filter(Boolean);

      if (correctWords.length === 0) {
        setFormError("Cümle içinde doğru kelimeleri yıldızla işaretleyin. Örnek: *Ankara*");
        return;
      }

      const distractorWords = (draft.wordBankDistractorsText || "")
        .split(/\n|,/)
        .map((v) => v.trim())
        .filter(Boolean);

      interaction = {
        id: editingInteractionId ?? makeId(),
        type: "word_bank_fill",
        timeSec: draft.timeSec,
        startTime: draft.timeSec,
        prompt: safeTrim(draft.prompt) || "Kelimeleri doğru kutulara sürükleyin",
        revealText: safeTrim(draft.revealText),
        backgroundImageUrl: safeTrim(draft.backgroundImageUrl),
        exampleImageUrl: safeTrim(draft.exampleImageUrl),
        position: draftPosition,
        sentenceTemplate: draft.wordBankSentenceText,
        correctWords,
        distractorWords,
        enableRetry: draft.enableRetry ?? true,
        showSolution: draft.showSolution ?? true

      };
    } else {
      const slots = dragDropSlots;
      const items = dragDropItems;

      const correctBySlot = (draft.dragDropCorrectMap || [])
        .slice(0, slots.length)
        .map((group) =>
          group.filter((idx, indexInGroup, arr) => {
            return (
              Number.isInteger(idx) &&
              idx >= 0 &&
              idx < items.length &&
              arr.indexOf(idx) === indexInGroup
            );
          })
        );

      const flattened = correctBySlot.flat();
      const unique = new Set(flattened);

      if (
        slots.length === 0 ||
        items.length === 0 ||
        correctBySlot.length !== slots.length ||
        correctBySlot.some((group) => group.length === 0) ||
        unique.size !== flattened.length
      ) {
        setFormError("Lütfen eklemeden önce her satıra bir veya daha fazla benzersiz öğe eşleyin.");
        return;
      }

      interaction = {
        id: editingInteractionId ?? makeId(),
        type: "drag_drop",
        timeSec: draft.timeSec,
        startTime: draft.timeSec,
        prompt: safeTrim(draft.prompt),
        revealText: safeTrim(draft.revealText),
        backgroundImageUrl: safeTrim(draft.backgroundImageUrl),
        exampleImageUrl: safeTrim(draft.exampleImageUrl),
        position: draftPosition,
        slots,
        items,
        correctBySlot,
        enableRetry: draft.enableRetry ?? true,
        showSolution: draft.showSolution ?? true

      };
    }

    setProject((prev) => {
      const updated =
        editingInteractionId == null
          ? [...(prev.interactions || []), interaction]
          : (prev.interactions || []).map((item) => {

            if (item.id !== editingInteractionId) return item;
          
            return {
              ...interaction,
              isDirty: true,
              isNew: item.isNew || false
            };
          
          })

      return {
        ...prev,
        interactions: updated.sort((a, b) => a.timeSec - b.timeSec)
      };
    });

    setDraft(getDefaultDraft(draft.type));
    setIsMediaAccordionOpen(false);
    setDraftPosition({ xPct: 50, yPct: 50 });
    setFormError("");
    setEditingInteractionId(null);
    setIsInteractionDrawerOpen(false);
  };

  const deleteInteraction = async (id) => {

    const interaction = project.interactions.find((i) => i.id === id);
    if (!interaction) return;
  
    try {
      const payload = {
        InteractionId: id
      }
  
      // Interaction exists in backend
      if (typeof id === "number") {
        setDeletedInteractionIds((prev) => [...prev, id]);
        const response = await deleteInteractionVideo(user.Token,payload);
  
        if (response?.data?.ResponseStatus !== 1) {
          setFormError(
            response?.data?.ResponseMessage || "Etkileşim silinemedi."
          );
          toast.error("Etkileşim silinemedi.")
          return;
        }
  
      }
  
      // Remove locally (both backend and unsaved interactions)
      setProject((prev) => ({
        ...prev,
        interactions: prev.interactions.filter((q) => q.id !== id)
      }));
  
      if (selectedPreviewInteractionId === id)
        setSelectedPreviewInteractionId(null);
  
      if (editingInteractionId === id)
        setEditingInteractionId(null);
  
    } catch (error) {
      console.error("Delete interaction failed:", error);
      setFormError("Sunucu hatası oluştu. Etkileşim silinemedi.");
    }
  };
  
  
  

  const openInteractionDrawer = () => {
    const currentVideo = previewRef.current;
    if (currentVideo && typeof currentVideo.pause === "function") currentVideo.pause();

    const currentSec = Number((currentVideo?.currentTime ?? 0).toFixed(3));
    setPreviewTimeSec(currentSec);

    const nextDraft = getDefaultDraft(draft.type);
    nextDraft.timeSec = currentSec;

    setDraft(nextDraft);
    setIsMediaAccordionOpen(false);
    setDraftPosition({ xPct: 50, yPct: 50 });
    setFormError("");
    setEditingInteractionId(null);
    setIsInteractionDrawerOpen(true);
  };

  const openEditInteractionDrawer = (id) => {
    const interaction = (project.interactions || []).find(
      (item) => item.id === id
    );
    if (!interaction) return;
  
    const video = previewRef.current;
  
    if (video) {
      video.pause();
      video.currentTime = interaction.timeSec;
    }
  
    setPreviewTimeSec(interaction.timeSec);
    fillDraftFromInteraction(interaction);
    setEditingInteractionId(id);
    setSelectedPreviewInteractionId(id);
    setFormError("");
    setIsInteractionDrawerOpen(true);
  };

  const openPreviewInteraction = (id) => {
    if (!isInteractionDrawerOpen && selectedPreviewInteractionId === id) {
      setSelectedPreviewInteractionId(null);
      return;
    }
  
    const interaction = (project.interactions || []).find((item) => item.id === id);
    if (!interaction) return;
  
    const video = previewRef.current;
    if (video) {
      video.pause();
      video.currentTime = interaction.timeSec;
    }
  
    setPreviewTimeSec(interaction.timeSec);
    setSelectedPreviewInteractionId(id);
    setEditingInteractionId(null);
    setIsInteractionDrawerOpen(false);
  };
  

  const closeInteractionDrawer = () => {
    setIsInteractionDrawerOpen(false);
    setEditingInteractionId(null);
    setIsDeleteConfirmOpen(false);
  };

  const askDeleteEditingInteraction = () => {
    if (!editingInteractionId) return;
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteEditingInteraction = () => {
    if (!editingInteractionId) return;
    deleteInteraction(editingInteractionId);
    setIsDeleteConfirmOpen(false);
    setIsInteractionDrawerOpen(false);
    setEditingInteractionId(null);
  };

  // const onLocalImagePicked = async (field, file) => {
  //   if (!file) return;

  //   if (file.size > MAX_IMAGE_SIZE_BYTES) {
  //     setFormError("Görsel boyutu en fazla 20 MB olabilir.");
  //     return;
  //   }

  //   try {
  //     const dataUrl = await readFileAsDataUrl(file);
  //     setDraft((prev) => ({ ...prev, [field]: dataUrl }));
  //   } catch {
  //     setFormError("Görsel yüklenemedi. Lütfen farklı bir dosya deneyin.");
  //   }
  // };
  async function onLocalImagePicked(field, file) {
    if (!file) return;
  
    try {
  
      setIsUploading(true);
  
     
  
      const uploadedUrl = await uploadImageToCMS(file, user.Token);
  
      if (!uploadedUrl) return;
  
      setDraft((prev) => ({
        ...prev,
        [field]: uploadedUrl
      }));
  
    } finally {
      setIsUploading(false);
    }
  }
  const syncVideoToDraftTime = (nextTimeSec) => {
    const video = previewRef.current;
    if (!video) {
      setPreviewTimeSec(nextTimeSec);
      return;
    }
    const safe =
      Number.isFinite(video.duration) && video.duration > 0
        ? clamp(nextTimeSec, 0, video.duration)
        : Math.max(0, nextTimeSec);
    video.currentTime = safe;
    video.pause();
    setPreviewTimeSec(safe);
  };

  const saveProjectToDashboard = () => {
    if (!isVideoEditable) {
      setFormError("Bu video durumu nedeniyle düzenlenemez.");
      return;
    }
  
    if (!builderData) {
      setFormError("Lütfen önce dashboard'dan bir video seçin.");
      return;
    }
  
    const result = saveVideoWork({
      video: {
        id: builderData.AchievementVideoId,
        title: builderData.AchievementVideoTitle,
        videoUrl: builderData.VideoHlsUrl || builderData.VideoUrl,
        status: currentVideoStatus
      },
      project,
      user: sessionUser
    });
  
    setCurrentVideoStatus(result.status);
    setProject(result.project);
    setSaveInfoText(`Kaydedildi. Durum: ${getStatusLabel(result.status)}`);
  
    if (onSaved) {
      onSaved(result.videoId);
    }
  };

//  const saveInteractions = async () => {
//   if (!builderData) return;

//   try {
//     setIsSaving(true);

//     const payload = buildSaveInteractionsPayload(
//       project,
//       builderData.AchievementVideoId
//     );

//     const response = await addAchVideoInteraction(user.Token,payload);

//     if (response?.data?.ResponseStatus === 1) {
//       setSaveInfoText("Etkileşimler başarıyla kaydedildi.");
//       setIsSaveConfirmOpen(false);
//     } else {
//       setFormError(response?.data?.ResponseMessage || "Kaydetme başarısız.");
//     }

//   } catch (error) {
//     console.error(error);
//     setFormError("Sunucu hatası oluştu.");
//   } finally {
//     setIsSaving(false);
//   }
// };

const saveInteractions = async () => {
  if (!builderData) return;

  try {
    setIsSaving(true);

    const newInteractions = project.interactions.filter(
      (i) => typeof i.id !== "number"
    );

    const existingInteractions = project.interactions.filter(
      (i) => typeof i.id === "number"
    );

    /* ADD NEW INTERACTIONS */

    if (newInteractions.length > 0) {
      const addPayload = buildSaveInteractionsPayload(
        { ...project, interactions: newInteractions },
        builderData.AchievementVideoId
      );

      await addAchVideoInteraction(user.Token,addPayload);
    }

    /* UPDATE EXISTING INTERACTIONS */

    for (const interaction of existingInteractions) {
      const updatePayload = buildUpdateInteractionPayload(interaction);

      await updateInteractionVideo(user.Token,updatePayload);
    }

    setSaveInfoText("Etkileşimler başarıyla kaydedildi.");
    setIsSaveConfirmOpen(false);

    // clear builder cache
    localStorage.removeItem(
      getBuilderStorageKey(builderData?.AchievementVideoId)
    );
  
    // short delay so user sees success
    setTimeout(() => {
      history.push({
        pathname: "/VideoInteractionDashboard/videos",
        state: {
          user:user,
          selectedCourse: selectedCourse
        }
      });
    }, 600);

  } catch (error) {
    toast.error("Kaydetme sırasında hata oluştu.")
    console.error(error);
    setFormError("Kaydetme sırasında hata oluştu.");
  } finally {
    setIsSaving(false);
  }
};

const updateInteractionStatus = async (interaction, status) => {
  try {

    setStatusUpdating((prev) => ({
      ...prev,
      [interaction.id]: true
    }));
    const updatePayload = buildInreviewInteractionPayload(interaction,status);

    await updateInteractionVideo(user.Token, updatePayload);

    setProject((prev) => ({
      ...prev,
      interactions: prev.interactions.map((i) =>
        i.id === interaction.id
          ? { ...i, status }
          : i
      )
    }));

  } catch (err) {
    toast.error("etkileşim Değerlendirme Sırasında Bir Hata Oluştu")
    console.error(err);
    setFormError("Durum güncellenemedi.");
  }finally {
    setStatusUpdating((prev) => ({
      ...prev,
      [interaction.id]: false
    }));
  }
};




  
  

  return (
    <div className="video-builder-scope">
<ToastContainer />
    <div className="layout">
    <header className="card">
      <h1>Etkileşimli Video Oluşturucu</h1>
     

      {builderData && (
          <p className="muted">
            Video: <strong>{builderData.AchievementVideoTitle}</strong> 
           
          </p>
        )}
        <div className="button-row">
        <button
    className="secondary"
    onClick={() =>
      history.push({
        pathname: "/VideoInteractionDashboard/videos",
        state: {
          user:user,
          selectedCourse: selectedCourse
        }
      })
    }
  >
    
    Geri
  </button>
          {userRole === "teacher"&&<button
            className="primary"
            onClick={() => setIsSaveConfirmOpen(true)}
            disabled={!isVideoEditable || project.interactions.length === 0}
          >
            Kaydet
          </button>}
          {onBack && <button onClick={onBack}>Dashboard'a Dön</button>}
        </div>
        {saveInfoText && <p className="ok">{saveInfoText}</p>}
    </header>

    <section className="card">
      <h2>Video Ayarları</h2>
      <label>
        Başlık
        <input
          value={project.title}
          onChange={(e) =>{
            const value = e.target.value
            setProject((prev) => ({ ...prev, title: value }))
          }
        }
        />
      </label>
      <label>
      Video URL (dashboard'dan gelir)
          <input value={project.videoUrl} readOnly />
      </label>
      {project.videoUrl && (
        <>
          <div className="builder-stage-layout">
            <div className="builder-video-column">
              <div className="builder-video-toolbar">
              {userRole === "teacher" && <button
                    className="primary"
                    onClick={openInteractionDrawer}
                    disabled={!isVideoEditable}
                  >
                  Etkileşim Ekle
                </button>}
                <span className="muted">
                Video zamanı: <strong>{toSecondsMsLabel(previewTimeSec)}</strong>
                </span>
              </div>
              <label>Önizleme</label>
              <div className="preview-stage" ref={previewStageRef}>
                <video
                  ref={previewRef}
                  controls
                  className="video"
                  src={project.videoUrl}
                  onLoadedMetadata={(e) => {
                    setVideoDuration(e.currentTarget.duration);
                  }}
                  onTimeUpdate={(e) =>
                    setPreviewTimeSec(e.currentTarget.currentTime)
                  }
                  // onTimeUpdate={(e) =>
                  //   setPreviewTimeSec(e.currentTarget.currentTime)
                  // }
                  onSeeked={(e) =>
                    setPreviewTimeSec(e.currentTarget.currentTime)
                  }
                  onClick={() => {
                    if (!isInteractionDrawerOpen && selectedPreviewInteractionId) {
                      setSelectedPreviewInteractionId(null);
                    }
                  }}
                />
                <div className="timeline-marker-layer">
  {sortedInteractions.map((interaction) => {
    const left = videoDuration
      ? (interaction.timeSec / videoDuration) * 100
      : 0;

    return (
      <div
        key={interaction.id}
        className="video-timeline-marker"
        style={{ left: `${left}%` }}
        onMouseDown={(e) => {
          e.stopPropagation();

          const video = previewRef.current;
          if (!video) return;

          video.currentTime = interaction.timeSec;
          video.pause();

          openPreviewInteraction(interaction.id);
        }}
      />
    );
  })}
</div>
                {isInteractionDrawerOpen && (
                  <button
                    className="position-marker"
                    style={{
                      left: `${draftPosition.xPct}%`,
                      top: `${draftPosition.yPct}%`
                    }}
                    onPointerDown={() => {
                      dragPointerActiveRef.current = true;
                    }}
                    title="Etkileşim konumunu sürükleyin"
                  >
                    + {interactionLabel(draft.type)}
                  </button>
                )}
                {!isInteractionDrawerOpen && selectedPreviewInteraction && (
                  <BuilderInteractionPreview
                    interaction={selectedPreviewInteraction}
                    onClose={() => setSelectedPreviewInteractionId(null)}
                  />
                )}
              </div>
              <p className="muted">
                `Etkileşim Ekle` butonuna bastığınız anda video durur ve o saniye
                otomatik olarak forma yazılır.
              </p>
            </div>
            <aside className="builder-interaction-panel">
              <h3>Etkileşimler</h3>
              {sortedInteractions.length === 0 && (
                <p className="muted">Henüz etkileşim yok.</p>
              )}
              {sortedInteractions.map((interaction, idx) => (
                <div
                  role="button"
                  tabIndex={0}
                  key={interaction.id}
                  className={`builder-interaction-item
                  ${selectedPreviewInteractionId === interaction.id ? "is-active" : ""}
                  ${interaction.InteractionStatus === 4 ? "is-rejected" : ""}
                `}
                  onClick={() => openPreviewInteraction(interaction.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openPreviewInteraction(interaction.id);
                    }
                  }}
                >
                  <div className="builder-interaction-item-head">
                    <strong>Etkileşim {idx + 1}</strong>
                    <div className="builder-interaction-item-head-right">
                 
                    <span>{toSecondsMsLabel(interaction.timeSec)}</span>
                     {userRole === "teacher" && <button
                        type="button"
                        className="builder-interaction-item-edit"
                        disabled={!isVideoEditable}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditInteractionDrawer(interaction.id);
                        }}
                      >
                        Düzenle
                      </button>}
                    </div>
                  </div>
                  {interaction.InteractionStatus && (
  <span
    className="interaction-status-badge"
    style={{
      background: InteractionStatusColors[interaction.InteractionStatus],
      color: "#fff",
      padding: "2px 8px",
      borderRadius: "8px",
      fontSize: "12px",
      marginRight: "8px"
    }}
  >
    {InteractionStatusLabels[interaction.InteractionStatus]}
  </span>
)}
                  <div className="builder-interaction-item-type">
                    {interactionLabel(interaction.type)}
                  </div>
                  <div className="builder-interaction-item-text">
                    {interaction.prompt || "(Metin yok)"}
                  </div>
                </div>
              ))}
            </aside>
          </div>
        </>
      )}
    </section>

    {isInteractionDrawerOpen && (
      <div className="interaction-drawer-backdrop" >
        <section
          className="card interaction-drawer"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="interaction-drawer-head">
            <h2>{editingInteractionId ? "Etkileşimi Düzenle" : "Etkileşim Ekle"}</h2>
            <button type="button" onClick={closeInteractionDrawer}>
              Kapat
            </button>
          </div>
          <p className="muted">
            Türü seçin, detayları doldurun ve konumu video üstündeki işaretçiden
            sürükleyin.
          </p>
          <BuilderDrawerForm
            draft={draft}
            setDraft={setDraft}
            previewRef={previewRef}
            isMediaAccordionOpen={isMediaAccordionOpen}
            setIsMediaAccordionOpen={setIsMediaAccordionOpen}
            setDraftPosition={setDraftPosition}
            setFormError={setFormError}
            onLocalImagePicked={onLocalImagePicked}
            dragDropSlots={dragDropSlots}
            dragDropItems={dragDropItems}
            formError={formError}
            editingInteractionId={editingInteractionId}
            addInteraction={addInteraction}
            askDeleteEditingInteraction={askDeleteEditingInteraction}
            onDraftTimeChange={syncVideoToDraftTime}
          />
        </section>
      </div>
    )}

    {isDeleteConfirmOpen && (
      <div
        className="confirm-modal-backdrop"
        onClick={() => setIsDeleteConfirmOpen(false)}
      >
        <div
          className="confirm-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <h3>Etkileşimi Sil</h3>
          <p>
            Bu etkileşimi silmek istediğinize emin misiniz? Bu işlem geri
            alınamaz.
          </p>
          <div className="confirm-modal-actions">
            <button
              type="button"
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              Vazgeç
            </button>
            <button
              type="button"
              className="danger-button"
              onClick={confirmDeleteEditingInteraction}
            >
              Evet, Sil
            </button>
          </div>
        </div>
      </div>
    )}
    {isSaveConfirmOpen && (
  <div className="confirm-modal-backdrop">
    <div className="confirm-modal large">

      <h3>Etkileşimleri Kaydetmeden Önce Kontrol Edin</h3>

      <p className="muted">
        Videoyu oynatarak etkileşimleri kontrol edebilirsiniz.
      </p>

      <div className="embed-preview">
        <iframe
          title="Etkileşimli video önizlemesi"
          src={embedUrl}
          allow="autoplay; fullscreen"
        />
      </div>

      <div className="confirm-modal-actions">
        <button onClick={() => setIsSaveConfirmOpen(false)}>
          Vazgeç
        </button>

        <button
          className="primary"
          disabled={isSaving}
          onClick={saveInteractions}
        >
          {isSaving ? "Kaydediliyor..." : "Onayla ve Kaydet"}
        </button>
      </div>

    </div>
  </div>
)}

{userRole === "admin" &&<section className="card">
      <h2>Zaman Çizelgesi</h2>
      {/* {project.interactions.length === 0 && (
        <p className="muted">Henüz etkileşim yok.</p>
      )} */}
      {[...project.interactions]
  .sort((a, b) => a.timeSec - b.timeSec)
  .map((q) => (
        <div className="timeline-item" key={q.id}>
        <div className="timeline-info">
          <strong>{toSecondsMsLabel(q.timeSec)}</strong> -{" "}
          {interactionLabel(q.type)}
          <div>{q.prompt}</div>
        </div>
      
        <div className="timeline-actions">

  {q.status ? (
    <span className={`status-label status-${q.status}`}>
      {q.status === 5 ? "Onaylandı" : "Reddedildi"}
    </span>
  ) : (
    <>
      <button
        className="reject-btn"
        disabled={statusUpdating[q.id]}
        onClick={() =>
          updateInteractionStatus(q, VideoInteractionStatus.Rejected)
        }
      >
        {statusUpdating[q.id] ? "..." : "Reddet"}
      </button>

      <button
        className="approve-btn"
        disabled={statusUpdating[q.id]}
        onClick={() =>
          updateInteractionStatus(q, VideoInteractionStatus.Approved)
        }
      >
        {statusUpdating[q.id] ? "..." : "Onayla"}
      </button>
    </>
  )}

</div>

      </div>
      
      ))}
    </section>}


    <section className="card">

      {hasVideoUrl && (
        <>
          <h3>Önizleme</h3>
          <p className="muted">
            Etkileşimleri görmek için oynatıp soru zamanlarına ilerleyin.
          </p>
          <div className="embed-preview">
            <iframe
              title="Etkileşimli video önizlemesi"
              src={embedUrl}
              allow="autoplay; fullscreen"
            />
          </div>
        </>
      )}
    </section>
  </div>
  </div>

  );
}
